import type { Ref } from 'vue';
import type { GridBlockPosition, GridLayoutData, ResizeState } from './grid-types';
import { computed, onUnmounted, ref } from 'vue';
import { safeUpdateGridData } from './grid-drag-utils';
import { useGridValidation } from './use-grid-validation';

/**
 * Grid resize composable
 * Handles left/right resize handles for grid blocks
 */
export function useGridResize(
	gridData: Ref<GridLayoutData>,
	updateGridData: (data: Partial<GridLayoutData>) => void,
) {
	const { validatePosition, tryAccommodate } = useGridValidation(gridData);

	const resizing = ref<ResizeState | null>(null);
	const previewPosition = ref<GridBlockPosition | null>(null);

	const isResizing = computed(() => resizing.value !== null);

	function startResize(
		event: MouseEvent,
		childId: string,
		handle: 'left' | 'right',
		rowElement: HTMLElement,
	) {
		event.stopPropagation();
		event.preventDefault();

		if (isResizing.value) return;

		const position = gridData.value.blockPositions[childId];
		if (!position) return;

		const row = gridData.value.rows[position.rowIndex];
		if (!row) return;

		const rowRect = rowElement.getBoundingClientRect();
		const cellWidth = rowRect.width / row.columns;

		resizing.value = {
			childId,
			handle,
			initialColStart: position.colStart,
			initialColSpan: position.colSpan,
			rowIndex: position.rowIndex,
			row,
			initialMouseX: event.clientX,
			cellWidth,
		};

		previewPosition.value = { ...position };

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);

		document.body.style.userSelect = 'none';
		document.body.style.cursor = 'col-resize';
	}

	function onMouseMove(event: MouseEvent) {
		if (!resizing.value) return;

		const { childId, handle, initialColStart, initialColSpan, initialMouseX, cellWidth, rowIndex, row } =
			resizing.value;

		const deltaX = event.clientX - initialMouseX;
		const deltaColumns = Math.round(deltaX / cellWidth);

		let newColStart = initialColStart;
		let newColSpan: number;

		if (handle === 'right') {
			newColSpan = Math.max(1, initialColSpan + deltaColumns);
		}
		else {
			const newStart = Math.max(0, initialColStart + deltaColumns);
			const startDelta = newStart - initialColStart;
			newColStart = newStart;
			newColSpan = Math.max(1, initialColSpan - startDelta);
		}

		const validation = validatePosition(rowIndex, newColStart, newColSpan, childId);

		if (validation.valid) {
			previewPosition.value = {
				rowIndex,
				colStart: newColStart,
				colSpan: newColSpan,
			};
		}
		else {
			const accommodation = tryAccommodate(childId, rowIndex, newColStart, newColSpan);

			if (accommodation.canAccommodate) {
				if (accommodation.strategy === 'add_column' && accommodation.updatedRows) {
					const scale = accommodation.updatedRows[rowIndex]!.columns / row.columns;

					previewPosition.value = {
						rowIndex,
						colStart: Math.floor(newColStart * scale),
						colSpan: Math.max(1, Math.floor(newColSpan * scale)),
					};
				}
				else {
					previewPosition.value = {
						rowIndex,
						colStart: newColStart,
						colSpan: newColSpan,
					};
				}
			}
		}
	}

	function onMouseUp() {
		if (!resizing.value || !previewPosition.value) {
			cleanup();
			return;
		}

		const { childId, rowIndex } = resizing.value;
		const newPosition = previewPosition.value;

		const validation = validatePosition(rowIndex, newPosition.colStart, newPosition.colSpan, childId);

		if (validation.valid) {
			const updatedPositions = { ...gridData.value.blockPositions, [childId]: newPosition };

			safeUpdateGridData(gridData.value, { blockPositions: updatedPositions }, updateGridData, '[GridResize]');
		}
		else {
			const accommodation = tryAccommodate(childId, rowIndex, newPosition.colStart, newPosition.colSpan);

			if (accommodation.canAccommodate) {
				const updates: Partial<GridLayoutData> = {};

				if (accommodation.updatedPositions) {
					updates.blockPositions = { ...accommodation.updatedPositions };
					updates.blockPositions[childId] = newPosition;
				}
				else {
					updates.blockPositions = { ...gridData.value.blockPositions };
					updates.blockPositions[childId] = newPosition;
				}

				if (accommodation.updatedRows) {
					updates.rows = accommodation.updatedRows;
				}

				safeUpdateGridData(gridData.value, updates, updateGridData, '[GridResize]');
			}
		}

		cleanup();
	}

	function cleanup() {
		document.removeEventListener('mousemove', onMouseMove);
		document.removeEventListener('mouseup', onMouseUp);
		document.body.style.userSelect = '';
		document.body.style.cursor = '';
		resizing.value = null;
		previewPosition.value = null;
	}

	onUnmounted(() => {
		cleanup();
	});

	return {
		resizing,
		isResizing,
		previewPosition,
		startResize,
	};
}
